import React, { useCallback } from "react";
import { showDialogAsync } from "../../dialog-queue";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useAppContext } from "../../../hooks/useAppContext";
import { useSelectedSpread } from "../../../hooks/useSelection";
import { useDefinition } from "../../../hooks/useDefinition";
import { useImporter } from "../../../contexts/importer-context";
import { useBusyBlockerContext } from "../../../contexts/busy-blocker-context";
import { useErrorFeedback } from "../../../hooks/useErrorFeedback";

function isMapPhotoSupportedOnDefinition(definition: eXpress.core.Definition | null) {
	return !!definition?.hasZoneForPhotoSourceType("map");
}

function getMapMenusAsync() {
	return import(
		/* webpackChunkName: "express-maps-menus" */
		"../../bottom-maps-menus"
	).then((module) => module.default);
}

let hasLoggedMissingConfig = false;

/** Hook that returns state or methods to work with Photo Maps */
export function useMapZones() {
	const { createErrorHandler } = useErrorHandling();
	const { errorFeedback } = useErrorFeedback();
	const dashboard = useDashboardContext();
	const {
		editor,
		platform: { ajax },
		context,
		localizedContent,
	} = useAppContext();
	const importer = useImporter();

	// TODO: listen to messages for possible changes (add/remove spread)
	const spread = useSelectedSpread();
	const isSupportedOnSpread =
		(!!context.osmTileServerUrl && !!spread?.hasZoneForPhotoSourceType("map")) ?? false;
	const { busyBlocker } = useBusyBlockerContext();

	// TODO: listen to messages for possible changes (add/remove zones)
	const definition = useDefinition();
	const hasMapInDefinition = isMapPhotoSupportedOnDefinition(definition);
	const isSupportedOnDefinition =
		!!context.osmTileServerUrl && !!context.osmMapboxAccessToken && hasMapInDefinition;

	if (
		!hasLoggedMissingConfig &&
		hasMapInDefinition &&
		(!context.osmTileServerUrl || !context.osmMapboxAccessToken)
	) {
		hasLoggedMissingConfig = true;
		const error = new eXpress.GeneralException(
			"MapConfigMissingException",
			`Product contains map zones, but is missing configuration:\n ${
				!context.osmTileServerUrl ? "no tile server configured \n" : ""
			} ${!context.osmMapboxAccessToken ? "missing accessToken for address search" : ""}`,
		);
		createErrorHandler()(error);
	}

	const openDialogAsync = useCallback(async () => {
		const currentSelectedZone = editor.selection.getCurrentZone();
		let currentSelectedPhotoZone: eXpress.core.PhotoZone | undefined;
		let currentSelectedPhotoZoneUpdateNeeded = false;

		if (
			!eXpress.core.isPhotoZone(currentSelectedZone) ||
			!currentSelectedZone.isZoneForPhotoSourceType("map")
		) {
			// fallback: take first map zone
			currentSelectedPhotoZone = spread?.getFillablePhotoZones(false, "map").first();

			if (!currentSelectedPhotoZone) {
				return Promise.resolve(undefined);
			}

			currentSelectedPhotoZoneUpdateNeeded = true;
		} else {
			currentSelectedPhotoZone = currentSelectedZone;
		}

		const { BottomPhotoMapsDialog } = await getMapMenusAsync();
		const defaultMaphotoSourceConfig = currentSelectedPhotoZone?.defaultPhotoSourcesConfig?.map;

		const volatileImageRef = currentSelectedPhotoZone.photo?.volatileImageRef;
		const mapPhotoSourceItem = eXpress.client.isMapPhotoSourceItem(volatileImageRef)
			? volatileImageRef
			: undefined;

		// If design contains a Map.Location metadata, we get a regional search location from CMS
		// and use it as default search location.
		// Example:
		// - Paris: be: Parijs, fr: Paris
		// - Capital: be: Brussels, fr: Paris
		if (!mapPhotoSourceItem?.location && defaultMaphotoSourceConfig?.locationKey) {
			const cmsKey = `Label.Maps.Location.Metadata.${defaultMaphotoSourceConfig.locationKey}`;
			const searchLocation = await busyBlocker.during(
				localizedContent.getKeyAsync({
					label: cmsKey,
					allowWithoutDefault: true,
					logMissingToServer: true,
				}),
			);
			if (searchLocation) {
				defaultMaphotoSourceConfig.location = searchLocation;
			}
		}

		// TODO: Lazy load the dialog from a separate bundled 'express-maps' file
		return showDialogAsync<eXpress.client.IMapPhotoSourceData | undefined>(
			({ resolve }, dialogProps) => (
				<BottomPhotoMapsDialog
					data={{
						...defaultMaphotoSourceConfig,
						...mapPhotoSourceItem,
					}}
					onOk={(data) => resolve(data)}
					onCancel={() => resolve(undefined)}
					{...dialogProps}
				/>
			),
		)
			.then(async (mapPhotoSourceData) => {
				if (!mapPhotoSourceData || !mapPhotoSourceData.zoom) return;

				const createMapPhotoSourceItemWithRetry = eXpress.utils.pipe(
					eXpress.client.MapPhotoSourceItem.fromRemoteUrlAsync.bind(
						eXpress.client.MapPhotoSourceItem,
					),
					eXpress.utils.withRetry({
						maxRetryCount: 3,
						addRetryCountToException: true,
					}),
				);

				const newMapPhotoSourceItem = await busyBlocker.during(
					createMapPhotoSourceItemWithRetry(mapPhotoSourceData, {
						ajax,
					}),
				);

				// Create Photo
				const photo = new eXpress.core.Photo(newMapPhotoSourceItem, editor.assets.photos);

				// Replace map photo in zone
				dashboard.applyPhotoToZone(currentSelectedPhotoZone, photo);

				// If the current zone was not a map zone, we need to set the new zone as current
				if (currentSelectedPhotoZoneUpdateNeeded) {
					editor.selection.setCurrentZoneAndBroadcast(currentSelectedPhotoZone);
				}

				// Add new photo in PhotoSelection list and upload
				editor.addToPhotoSelection([photo], (photos) => {
					importer?.addToQueue(photos);
				});
			})
			.catch((err: unknown) => {
				errorFeedback();
				createErrorHandler()(err);
			});
	}, [
		spread,
		busyBlocker,
		localizedContent,
		ajax,
		editor,
		dashboard,
		importer,
		errorFeedback,
		createErrorHandler,
	]);

	return {
		isMapsSupportedOnDefinition: isSupportedOnDefinition,
		/** undefined if not allowed on the current spread */
		openZoneMapAsync: isSupportedOnSpread ? openDialogAsync : undefined,
	};
}
